import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Container, Typography, TextField, Button, Table, TableHead, TableRow, 
  TableCell, TableBody, Link as MuiLink, Box, Paper, InputAdornment, 
  Chip, TableContainer, Card, CardContent, CircularProgress, Alert,
  Pagination, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { bunkeringApi } from '../api/bunkering';
import { DOCUMENT_TYPE_LABELS } from '../api/documents';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

export default function Bunkerings() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  const [bunkerings, setBunkerings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters and pagination
  const [filterImo, setFilterImo] = useState('');
  const [filterUploadedBy, setFilterUploadedBy] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 10;

  // Initialize filters from URL parameters and fetch data
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const vesselImo = params.get('vessel');
    
    if (vesselImo) {
      setFilterImo(vesselImo);
      setPage(1); // Reset to first page when vessel filter is applied from URL
    } else {
      // Clear the filter if no vessel parameter in URL
      setFilterImo('');
    }
    
    // Fetch with the current state, but override vessel_imo if it's in URL
    fetchBunkeringsWithFilters(vesselImo || filterImo);
  }, [location.search]);

  // Fetch bunkerings when filters change (but not on initial load)
  useEffect(() => {
    // Only fetch if this is not the initial load (location.search effect handles that)
    if (location.search === '' || !location.search.includes('vessel=')) {
      fetchBunkerings();
    }
  }, [page, filterImo, filterUploadedBy, filterDateFrom, filterDateTo]);

  const fetchBunkeringsWithFilters = async (overrideVesselImo = null) => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        page,
        per_page: perPage,
        vessel_imo: overrideVesselImo || filterImo || undefined,
        uploaded_by: filterUploadedBy || undefined,
        date_from: filterDateFrom || undefined,
        date_to: filterDateTo || undefined
      };

      const response = await bunkeringApi.getBunkerings(params);
      const data = response.data.data || [];
      const meta = response.data.meta || {};
      
      setBunkerings(data);
      setTotalPages(meta.total_pages || 1);
      setTotalItems(meta.total || data.length);
      
    } catch (err) {
      console.error("Failed to fetch bunkerings:", err);
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        setError(errorData.detail || errorData.title || 'Failed to load bunkering events.');
      } else {
        setError('Failed to load bunkering events.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBunkerings = async () => {
    return fetchBunkeringsWithFilters();
  };

  const handleFilterChange = () => {
    setPage(1); // Reset to first page when filters change
    fetchBunkerings();
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getDocumentCountChip = (documentCount) => {
    return (
      <Chip 
        label={documentCount}
        size="small"
        color="default"
        icon={<InsertDriveFileIcon />}
      />
    );
  };

  // Check user permissions for actions
  const canUpload = user && (user.role === 'SHIPOWNER' || user.role === 'SUPPLIER');

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DirectionsBoatIcon sx={{ fontSize: 32, color: '#1565c0' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Bunkering Events
          </Typography>
        </Box>
        
        {canUpload && (
          <Button
            variant="contained"
            component={RouterLink}
            to="/bunkerings/new"
            startIcon={<UploadFileIcon />}
            sx={{ textTransform: 'none' }}
          >
            Create Bunkering
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filter Bunkering Events
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 2 }}>
            <TextField
              label="Vessel IMO"
              value={filterImo}
              onChange={(e) => setFilterImo(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              label="Uploaded By"
              value={filterUploadedBy}
              onChange={(e) => setFilterUploadedBy(e.target.value)}
              size="small"
            />
            
            <TextField
              label="Date From"
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Date To"
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          
          <Button
            variant="outlined"
            onClick={handleFilterChange}
            size="small"
          >
            Apply Filters
          </Button>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Bunkerings Table */}
      {!loading && (
        <>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vessel</TableCell>
                    <TableCell>Bunkering Date</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Documents</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bunkerings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" sx={{ py: 4 }}>
                          No bunkering events found matching your criteria.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bunkerings.map((bunkering) => (
                      <TableRow key={bunkering.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {bunkering.vessel_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              IMO: {bunkering.vessel_imo}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formatDate(bunkering.bunkering_date)}
                        </TableCell>
                        <TableCell>
                          {bunkering.created_by?.name || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          {getDocumentCountChip(bunkering.document_count)}
                        </TableCell>
                        <TableCell>
                          <Button
                            component={RouterLink}
                            to={`/bunkerings/${bunkering.id}`}
                            size="small"
                            startIcon={<VisibilityIcon />}
                            sx={{ textTransform: 'none' }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Results Summary */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Showing {bunkerings.length} of {totalItems} bunkering events
          </Typography>
        </>
      )}
    </Container>
  );
}